/**
 * Persian aliases for English SQL column keys.
 * Single catalog: `data/columnAliases.json` (sales.fields fa merged with
 * report-maker short names). UI shows Persian; SQL keeps English identifiers.
 */
import COLUMN_ALIASES from '../data/columnAliases.json';

export const COLUMN_LABELS_MAP = COLUMN_ALIASES;

/**
 * Cause id/name and leftover unsplit cause totals — data is aggregated into
 * ReturnCauseVocher* / PrizeVocher* / SellReturn* measures, so these must not
 * appear in report-maker pickers.
 */
export const HIDDEN_REPORT_COLUMNS = new Set([
  'return_cause_id',
  'return_cause_name',
  'sell_ret_cause_id',
  'sell_ret_cause_name',
  'return_cause_qty',
  'return_cause_pack_qty',
  'return_cause_prize_pack_qty',
  'return_cause_amount',
  'return_cause_net_amount',
]);

export function isHiddenReportColumn(key) {
  return HIDDEN_REPORT_COLUMNS.has(String(key || '').toLowerCase());
}

/** English SQL identifier (column key), not a Persian alias. */
export function isSqlColumnKey(key) {
  return /^[A-Za-z_][A-Za-z0-9_]*$/.test(String(key || ''));
}

/**
 * Output-column name from a widget query: English ident or Unicode/Persian
 * aliases such as برند / تارگت_ماه. Catalog aliases stay English via isSqlColumnKey.
 */
export function isResultColumnKey(key) {
  const s = String(key || '').trim();
  if (!s || s === '*') return false;
  return /^[\p{L}_][\p{L}\p{N}_]*$/u.test(s);
}

function normalizeLabel(s) {
  return String(s || '')
    .replace(/[\u200c\u200d]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function visibleColumnKeys(keys = []) {
  return (keys || []).filter((k) => k && isResultColumnKey(k) && !isHiddenReportColumn(k));
}

export function colLabel(key) {
  if (!key) return '';
  return COLUMN_LABELS_MAP[key] || key;
}

/** Every known English column key (for suggestion catalogs). */
export function knownColumnKeys() {
  return Object.keys(COLUMN_LABELS_MAP).filter((k) => !isHiddenReportColumn(k));
}

function isCustomColumnKey(key) {
  return /^custom_\d+$/.test(String(key || ''));
}

/** True when the stored header is empty or still the English SQL name. */
export function isDefaultEnglishLabel(label, key) {
  const l = normalizeLabel(label);
  if (!l) return true;
  if (key && l === String(key)) return true;
  return isSqlColumnKey(l) && !!COLUMN_LABELS_MAP[l];
}

/**
 * Header text: custom Persian stays; empty / English keys become the alias.
 */
export function displayLabel(colOrKey, fallbackKey) {
  if (colOrKey == null) return '';
  if (typeof colOrKey === 'string') return colLabel(colOrKey) || colOrKey;
  const key = colOrKey.key || fallbackKey;
  if (isDefaultEnglishLabel(colOrKey.label, key)) {
    return colLabel(key) || String(colOrKey.label || '').trim() || key || '';
  }
  return String(colOrKey.label).trim();
}

/** Fill Persian alias on a column config when the label is still English. */
export function withPersianLabel(col) {
  if (!col) return col;
  if (typeof col === 'string') {
    return { key: col, label: colLabel(col) };
  }
  const key = col.key;
  if (isCustomColumnKey(key) || col.source === 'custom') {
    if (!normalizeLabel(col.label)) {
      return { ...col, label: 'ستون سفارشی' };
    }
    return col;
  }
  if (!key) return col;
  if (isDefaultEnglishLabel(col.label, key)) {
    return { ...col, label: colLabel(key) };
  }
  return col;
}

/**
 * Map a UI token back to the stored column key.
 * Catalog Persian aliases resolve to English SQL names. Quoted/Persian
 * result aliases from a widget query stay as-is when they are known keys.
 */
export function resolveColumnKey(token, knownKeys = [], labelFn = colLabel) {
  const t = normalizeLabel(token);
  if (!t) return '';
  const known = (knownKeys || []).filter(Boolean);
  if (isSqlColumnKey(t) && (known.includes(t) || COLUMN_LABELS_MAP[t])) return t;
  const fromKnown = known.find((k) => isSqlColumnKey(k) && normalizeLabel(labelFn(k)) === t);
  if (fromKnown) return fromKnown;
  const fromMap = Object.keys(COLUMN_LABELS_MAP).find((k) => normalizeLabel(COLUMN_LABELS_MAP[k]) === t);
  if (fromMap) return fromMap;
  if (known.includes(t)) return t;
  return isResultColumnKey(t) ? t : '';
}
